import React from 'react';
import {
  Box, Container, Grid, Typography, Divider, IconButton, Button,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import ConstructionIcon from '@mui/icons-material/Construction';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLang } from '../context/LanguageContext';

const socialLinks = [
  { icon: InstagramIcon, label: 'Instagram', href: 'https://www.instagram.com/reliable_constructioncorp/' },
];

const navHrefs = ['#about', '#services', '#portfolio', '#testimonials', '#contact'];

export default function Footer() {
  const { t } = useLang();
  const fo = t.footer;

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box component="footer" sx={{ background: '#0A0A0A', pt: { xs: 7, md: 9 }, pb: 4 }}>
      <Container maxWidth="xl">

        {/* Top CTA strip */}
        <Box sx={{
          background: '#C8102E',
          p: { xs: 4, md: 5 },
          mb: { xs: 6, md: 8 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          gap: 3,
        }}>
          <Box>
            <Typography sx={{
              fontFamily: '"Oswald", sans-serif',
              color: '#fff',
              fontSize: { xs: '1.3rem', md: '1.7rem' },
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              lineHeight: 1.2,
            }}>
              {fo.ctaHeading}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5, fontWeight: 300, fontSize: '0.92rem' }}>
              {fo.ctaSub}
            </Typography>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap" flexShrink={0}>
            <Button
              variant="contained"
              onClick={() => scrollTo('#contact')}
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: '#fff',
                color: '#C8102E',
                whiteSpace: 'nowrap',
                '&:hover': { background: '#111', color: '#fff' },
              }}
            >
              {fo.ctaBtn}
            </Button>
            <Button
              variant="outlined"
              href={`tel:${fo.phone.replace(/\D/g, '')}`}
              startIcon={<PhoneIcon />}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.5)',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
              }}
            >
              {fo.phone}
            </Button>
          </Box>
        </Box>

        {/* Main grid */}
        <Grid container spacing={{ xs: 4, md: 6 }} mb={6}>

          {/* Company info */}
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
              <Box sx={{ background: '#C8102E', p: 0.7, display: 'flex', alignItems: 'center' }}>
                <ConstructionIcon sx={{ color: '#fff', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: '"Oswald", sans-serif', color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.1 }}>
                  Reliable
                </Typography>
                <Typography sx={{ fontFamily: '"Oswald", sans-serif', color: '#555', fontWeight: 400, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  Maintenance & Construction Corp
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.9, mb: 3, fontWeight: 300, fontSize: '0.85rem' }}>
              {fo.about}
            </Typography>

            <Box display="flex" gap={1}>
              {socialLinks.map((s) => {
                const Icon = s.icon;
                return (
                  <IconButton
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    sx={{
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 0,
                      color: '#555',
                      width: 36,
                      height: 36,
                      '&:hover': { background: '#C8102E', color: '#fff', borderColor: '#C8102E' },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Icon sx={{ fontSize: 17 }} />
                  </IconButton>
                );
              })}
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography sx={{ fontFamily: '"Oswald", sans-serif', color: '#fff', fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', mb: 2.5, fontWeight: 500 }}>
              {fo.quickLinks}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.2}>
              {fo.navLabels.map((label, i) => (
                <Typography
                  key={label}
                  onClick={() => scrollTo(navHrefs[i])}
                  sx={{ color: '#555', fontSize: '0.85rem', cursor: 'pointer', transition: 'color 0.2s ease', '&:hover': { color: '#C8102E' } }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Services */}
          <Grid item xs={6} sm={4} md={3}>
            <Typography sx={{ fontFamily: '"Oswald", sans-serif', color: '#fff', fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', mb: 2.5, fontWeight: 500 }}>
              {fo.servicesLabel}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.2}>
              {fo.services.map((s) => (
                <Typography
                  key={s}
                  onClick={() => scrollTo('#services')}
                  sx={{ color: '#555', fontSize: '0.85rem', cursor: 'pointer', transition: 'color 0.2s ease', '&:hover': { color: '#C8102E' } }}
                >
                  {s}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Service Area */}
          <Grid item xs={12} sm={4} md={3}>
            <Typography sx={{ fontFamily: '"Oswald", sans-serif', color: '#fff', fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', mb: 2.5, fontWeight: 500 }}>
              {fo.serviceArea}
            </Typography>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.9, mb: 2, fontSize: '0.85rem' }}>
              {fo.serviceAreaText}
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.8}>
              {fo.areas.map((area) => (
                <Typography key={area} variant="body2" sx={{ color: '#555', fontSize: '0.82rem' }}>
                  · {area}
                </Typography>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 4 }} />

        {/* Bottom bar */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap={2}
        >
          <Typography variant="caption" sx={{ color: '#333' }}>
            {fo.copyright}
          </Typography>
          <Box display="flex" gap={3} flexWrap="wrap">
            {fo.links.map((item) => (
              <Typography
                key={item}
                variant="caption"
                sx={{ color: '#333', cursor: 'pointer', '&:hover': { color: '#C8102E' }, transition: 'color 0.2s' }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
